import { __ } from '@wordpress/i18n';
import { useSelect } from '@wordpress/data';
import { useBlockEditingMode } from '@wordpress/block-editor';
import { useRef, useState, useEffect } from '@wordpress/element';
import { applyFilters } from '@wordpress/hooks';
import { DynamicStyleIcon, DynamicmainStyleIcon, captionmainstyles } from "./advance/inline-style";
import PanelSettings from './advance/inspector-control';
import AdvanceSettings from './advance';
import { CustomInserterModal, InserterModal } from './components';
import { flattenIconsArray } from './utils';
import { bolt as defaultIcon } from './icons/bolt';
import getIcons from './icons';
import { CustomBlockControls } from './blockControls';
import { VayuBlocksDevice } from '@vayu-blocks/device-control';
import { VayuBlocksUniqueId, VayuBlocksInfiniteAnimation, VayuBlocksTypography } from '@vayu-blocks/global';
import { VayuBlockAdvanceStyle } from '@vayu-blocks/advance-panel/style.js';
import { VideoHtml, MouseFollower } from '@vayu-blocks';
import { useMemo } from 'react';

export function Edit({ attributes, setAttributes, isSelected, clientId }) {
	VayuBlocksUniqueId({ setAttributes, clientId });

	const view = VayuBlocksDevice();
	const { iconName } = attributes;
	const { hoverAnimationClass } = VayuBlockAdvanceStyle(attributes);

	const { allowedMimeTypes } = useSelect((select) => {
		const { getSettings } = select('core/block-editor');
		return {
			allowedMimeTypes: getSettings().allowedMimeTypes,
		};
	}, []);

	const [isInserterOpen, setInserterOpen] = useState(false);
	const [isCustomInserterOpen, setCustomInserterOpen] = useState(false);
	const [isHovered, setIsHovered] = useState('normal');
	const [istextHovered, setIstextHovered] = useState('normal');
	const [isClicked, setIsClicked] = useState(false);
	const [captionstyle, setCaptionstyle] = useState([]);
	const [twowave, setTwowave] = useState('');

	const ref = useRef();
	const iconRef = useRef();

	const DynamicStyleIcons = DynamicStyleIcon({ attributes, isHovered });
	const DynamaicmainStyleIcon = DynamicmainStyleIcon({ attributes, isHovered });
	const captionmainstyle = captionmainstyles({ attributes, istextHovered });

	const isSVGUploadAllowed = allowedMimeTypes ? Object.values(allowedMimeTypes).includes('image/svg+xml') : false;
	const enableCustomIcons = applyFilters('iconBlock.enableCustomIcons', true);
	const isContentOnlyMode = useBlockEditingMode() === 'contentOnly';

	const iconsAll = flattenIconsArray(getIcons());
	const namedIcon = iconsAll.find((i) => i.name === iconName);
	const printedIcon = useMemo(() => namedIcon?.icon || defaultIcon, [iconName, iconsAll]);

	const handleClick = () => {
		setIsClicked(!isClicked);
	};

	const getAnimationClass = (effectType, value) => {
		const map = {
			'always': {
				'shadow-pulse': 'vayu_blocks_shadow_pulse_animation',
				'wave-pulse': 'vayu_blocks_wave_pulse_animation',
				'color-pulse': 'vayu_blocks_color_pulse_animation',
				'glow-pulse': 'vayu_blocks_glow_pulse_animation',
				'shadow-expand': 'vayu_blocks_shadow_expand_animation',
				'radial-glow': 'vayu_blocks_radial_glow_animation',
				'ripple-wave': 'vayu_blocks_ripple_wave_animation',
				'concentric': 'vayu_blocks_concentric_animation',
				'two-wave': 'vayu_blocks-two-wave',
			},
			'onhvr': {
				'shadow-pulse': 'vayu_blocks_shadow_pulse_animation-on-hover',
				'wave-pulse': 'vayu_blocks_wave_pulse_animation-on-hover',
				'color-pulse': 'vayu_blocks_color_pulse_animation-on-hover',
				'glow-pulse': 'vayu_blocks_glow_pulse_animation-on-hover',
				'shadow-expand': 'vayu_blocks_shadow_expand_animation-on-hover',
				'radial-glow': 'vayu_blocks_radial_glow_animation-on-hover',
				'ripple-wave': 'vayu_blocks_ripple_wave_animation-on-hover',
				'concentric': 'vayu_blocks_concentric_animation-on-hover',
				'two-wave': 'vayu_blocks-two-wave-hvr',
			},
		};
		return map?.[effectType]?.[value] || '';
	};

	useEffect(() => {
		const { background } = attributes.animationData || {};
		if (background?.value === 'two-wave') {
			setTwowave(background?.type === 'onhvr' ? 'vayu_blocks-two-wave-con-hvr' : 'vayu_blocks-two-wave-con');
		} else {
			setTwowave('');
		}
	}, [attributes.animationData]);

	useEffect(() => {
		setCaptionstyle(VayuBlocksTypography(attributes.typography, view));
	}, [attributes.typography]);

	const validParents = ['vayu-blocks/video', 'vayu-blocks/image-hotspot'];

	const parentBlockName = useSelect((select) => {
		const { getBlockParents, getBlock } = select('core/block-editor');
		const parents = getBlockParents(clientId);
		for (let i = 0; i < parents.length; i++) {
			const parentBlock = getBlock(parents[i]);
			if (validParents.includes(parentBlock?.name)) return parentBlock.name;
		}
		return null;
	}, [clientId]);

	useEffect(() => {
		if (parentBlockName) {
			setAttributes({ parentBlock: parentBlockName });
		}
	}, [parentBlockName]);

	const iconMarkup = attributes.iconName === 'medf' && attributes.mediaIcon ? (
		<div
			className={`vb-icon-main-svg ${VayuBlocksInfiniteAnimation(attributes.animationData)}`}
			ref={iconRef}
			style={{
				transform: `${attributes.flipHorizontal ? 'scaleX(-1)' : ''}${attributes.flipVertical ? ' scaleY(-1)' : ''}`,
			}}
			// 👇 Only outer div — no aria props
			dangerouslySetInnerHTML={{
				__html: attributes.mediaIcon.replace(
					/<svg/i,
					`<svg role="${attributes.mode}" aria-label="${(attributes.textmode || 'label')}" tabIndex="0" 
						stroke="${attributes.stroke || 'currentColor'}"
						stroke-width="${attributes.strokeWidth || 0}"
						stroke-dasharray="${(attributes.strokedashlength || 0)}, ${(attributes.strokedashgap || 0)}"`
				),
			}}
		/>
	) : (
		<div
			className={`vb-icon-main-svg ${VayuBlocksInfiniteAnimation(attributes.animationData)}`}
			ref={iconRef}
			style={{
				transform: `${attributes.flipHorizontal ? 'scaleX(-1)' : ''}${attributes.flipVertical ? ' scaleY(-1)' : ''}`,
			}}
		>
			{React.cloneElement(printedIcon, {
				role: attributes.mode,
				'aria-label': attributes.textmode|| 'label',
				tabIndex: 0,
				stroke: attributes.stroke || 'currentColor',
				strokeWidth: attributes.strokeWidth || 0,
				strokeDasharray: `${attributes.strokedashlength || 0}, ${attributes.strokedashgap || 0}`,
			})}
		</div>
	);

	return (
		<>
			<PanelSettings clientId={clientId} attributes={attributes} setAttributes={setAttributes} clickBorderHover={setIsHovered} isSelected={isSelected} />
			<AdvanceSettings attributes={attributes}>
				<VideoHtml bg={attributes.advBackground} />

				{isSelected && (
					<CustomBlockControls
						icon={printedIcon}
						setAttributes={setAttributes}
						isContentOnlyMode={isContentOnlyMode}
						enableCustomIcons={enableCustomIcons}
						isSVGUploadAllowed={isSVGUploadAllowed}
						setInserterOpen={setInserterOpen}
						setCustomInserterOpen={setCustomInserterOpen}
						attributes={attributes}
					/>
				)}

				<div className={`vb-icon-block-main-container ${twowave} ${hoverAnimationClass}`} style={DynamicStyleIcons}>
					<div style={DynamaicmainStyleIcon} className={`vb-icon-animation ${getAnimationClass(attributes.animationData?.background?.type, attributes.animationData?.background?.value)}`} />
					<div
						isHovered={isHovered}
						className="vb-icon-markup"
						onClick={handleClick}
						onMouseEnter={() => setIsHovered('hover')}
						onMouseLeave={() => setIsHovered('normal')}
						ref={ref}
					>
						{iconMarkup}
						{attributes.icontextallow && (
							<div
								style={captionmainstyle}
								isHovered={istextHovered}
								className="vb-icon-text"
								onMouseEnter={() => setIstextHovered('hover')}
								onMouseLeave={() => setIstextHovered('normal')}
							>
								<text className="vb-icon-text-text" style={captionstyle}>{attributes.icontxt}</text>
							</div>
						)}
					</div>
				</div>

				<MouseFollower value={attributes.advFollower} clientId={clientId} />
			</AdvanceSettings>

			<InserterModal isInserterOpen={isInserterOpen} setInserterOpen={setInserterOpen} attributes={attributes} setAttributes={setAttributes} />

			{enableCustomIcons && (
				<CustomInserterModal
					isCustomInserterOpen={isCustomInserterOpen}
					setCustomInserterOpen={setCustomInserterOpen}
					attributes={attributes}
					setAttributes={setAttributes}
				/>
			)}
		</>
	);
}

export default Edit;